/* /referrals — mã và link giới thiệu của học viên (gateway affiliate api.sutucon.com). */
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Icon, type IconName } from "@/components/icon"
import { ErrorState, LoadingState, Page, PageHeader, StatusNote, usePageTitle } from "@/components/page"
import { useSession } from "@/app/session"
import { affiliateGet, loginUrl } from "@/lib/api"

function LinkRow({ icon, title, host, copied, onCopy }: { icon: IconName; title: string; host: string; copied: boolean; onCopy: () => void }) {
  return (
    <button
      type="button"
      onClick={onCopy}
      className="flex w-full items-center gap-3 rounded-2xl border-[1.5px] border-edge bg-card p-3 text-left transition-colors hover:border-orange"
    >
      <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-soft">
        <Icon name={icon} className="size-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-semibold text-ink">{title}</span>
        <span className="block truncate text-sm text-muted-foreground">{host}</span>
      </span>
      <span className="text-sm font-semibold text-orange-strong">{copied ? "Đã chép" : "Sao chép"}</span>
    </button>
  )
}

export default function ReferralsPage() {
  usePageTitle("Giới thiệu bạn bè", "Gửi link giới thiệu Simba cho bạn học và phụ huynh.")
  const { loading, user } = useSession()
  const [data, setData] = useState<any>(null)
  const [error, setError] = useState("")
  const [copied, setCopied] = useState("")

  const load = () => {
    setError("")
    affiliateGet("/api/user/affiliate/stats")
      .then(setData)
      .catch((e: any) => setError(e.message || "Chưa tải được link giới thiệu."))
  }
  useEffect(() => {
    if (loading) return
    if (!user) {
      location.href = loginUrl(`${location.origin}/referrals`)
      return
    }
    load()
  }, [loading, user])

  const copy = async (label: string, value?: string) => {
    if (!value) return
    try {
      await navigator.clipboard.writeText(value)
      setCopied(label)
      window.setTimeout(() => setCopied(""), 1800)
    } catch {
      setError("Trình duyệt chưa cho phép sao chép. Con thử lại nhé.")
    }
  }

  if (loading || !user) return <LoadingState label="Đang mở link giới thiệu…" />

  const stats = [
    { value: data?.stats?.total_clicks || 0, label: "lượt mở link" },
    { value: data?.stats?.total_conversions || 0, label: "người đăng nhập" },
    { value: data?.model?.affinity_score || 0, label: "referral score" },
  ]

  return (
    <Page>
      <PageHeader
        eyebrow="Referral"
        title="Giới thiệu bạn bè học cùng Simba"
        description="Gửi link cho bạn học hoặc phụ huynh. Khi người được giới thiệu đăng nhập, Simba sẽ ghi nhận evidence cho hành vi đóng góp của con."
      />
      {error && !data ? (
        <ErrorState message={error} onRetry={load} />
      ) : !data ? (
        <LoadingState label="Đang chuẩn bị link riêng của con…" />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <Card className="bg-soft md:col-span-2">
            <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <img src="/brand/sutucon/mascots-2d/meerkat-happy.svg" alt="" className="size-16 shrink-0" />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="text-sm font-semibold tracking-wide text-accent-foreground uppercase">Mã giới thiệu</span>
                <strong className="font-heading text-3xl break-all text-ink">{data.referral_code}</strong>
              </div>
              <Button onClick={() => copy("Mã", data.referral_code)}>
                <Icon name={copied === "Mã" ? "check" : "share"} /> {copied === "Mã" ? "Đã sao chép" : "Sao chép mã"}
              </Button>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Link để gửi</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <LinkRow icon="users" title="Gửi cho bạn học" host="simba.sutucon.com" copied={copied === "Learner"} onCopy={() => copy("Learner", data.links?.learner)} />
              <LinkRow icon="home" title="Gửi cho phụ huynh" host="kiara.sutucon.com" copied={copied === "Parent"} onCopy={() => copy("Parent", data.links?.parent)} />
              {error && <StatusNote tone="error">{error}</StatusNote>}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Hoạt động giới thiệu</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="grid grid-cols-3 gap-2">
                {stats.map((s) => (
                  <div key={s.label} className="flex flex-col items-center gap-1 rounded-2xl bg-paper p-3 text-center">
                    <strong className="font-heading text-2xl text-ink">{s.value}</strong>
                    <span className="text-sm text-muted-foreground">{s.label}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">{data.model?.label || "Chưa có tín hiệu referral rõ ràng."}</p>
            </CardContent>
          </Card>
        </div>
      )}
    </Page>
  )
}
